import { Cell, Row, type RowProps, Text, TextSize } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import type { UserData } from '../users-table/users-table.types';
import { rowClasses } from './user-row.classes';
import { UserRowLoading } from './user-row.loading';

const contentClasses = cva({
  base: 'transition-opacity duration-150',
  variants: {
    disabled: {
      false: 'opacity-100',
      true: 'opacity-50',
    },
  },
});

/**
 * Props for the UserRow component
 */
interface UserRowProps extends RowProps<object> {
  /**
   * The user data to display in this row
   *
   * @required
   * @type UserData
   */
  user: UserData;
  /**
   * Additional CSS classes to apply to the component
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Whether the row is disabled
   *
   * @optional
   * @type boolean
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the row is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Ref to the row element
   *
   * @optional
   * @type (element: HTMLTableRowElement) => void
   */
  ref?: (element: HTMLTableRowElement) => void;
}

/**
 * UserRow displays user information as a table row with a cell for email address.
 * Supports interactive hover/focus states and click handling.
 *
 * Designed to work with TanStack Table and React Aria Table components.
 *
 * @param props - The props for the UserRow component
 * @example
 * // Used within a Table component
 * <Table>
 *   <TableHeader>
 *     <Column>Email Address</Column>
 *   </TableHeader>
 *   <TableBody>
 *     <UserRow
 *       user={{
 *         id: "user-123",
 *         email_address: "john.doe@example.com"
 *       }}
 *       onRowClick={(id) => console.log('View user:', id)}
 *     />
 *   </TableBody>
 * </Table>
 */
function UserRow(props: UserRowProps) {
  const { user, className, isLoading, isDisabled = false, ref, ...other } = props;

  if (isLoading) {
    return <UserRowLoading />;
  }

  return (
    <Row
      className={cx(rowClasses({ disabled: isDisabled }), className)}
      aria-label={`User: ${user.email_address}`}
      data-user-id={user.id}
      isDisabled={isDisabled}
      ref={ref}
      {...other}
    >
      <Cell className="py-8 px-12 w-full">
        <div className={cx('flex items-center', contentClasses({ disabled: isDisabled }))}>
          <Text size={TextSize.Md} maxLines={1} showEllipsis={true}>
            {user.email_address}
          </Text>
        </div>
      </Cell>
    </Row>
  );
}

export { UserRow };
export type { UserRowProps };
